import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Divider,
  Input,
  Tooltip,
  Typography,
  message,
  Spin,
  Modal,
  Tag,
  Popover,
  Empty,
} from "antd";
import {
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  EnterOutlined,
  SmileOutlined,
  PlayCircleOutlined,
  FilePdfOutlined,
  FileWordOutlined,
} from "@ant-design/icons";
import PaperClipIcon from "../../../../Theme/CustomIcons/Icons/Type,Paragraph,Character/PaperClipIcon";
import DocumentTexIcon from "../../../../Theme/CustomIcons/Icons/ContentEdit/DocumentTextIcon";
import SyncIcon from "../../../../Theme/CustomIcons/Icons/Arrow/SyncIcon";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import ChatMessage from "./ChatMessage";
import {
  adminCreateSupportMessageAction,
  adminUpdateSupportTicketAction,
  adminGetSupportMessageByTicketIdAction,
} from "../../../../store/action/admin/supportTicketActions.js";

const { TextArea } = Input;
const { Text, Title } = Typography;

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [
  "docx",
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "mp4",
  "mov",
  "avi",
  "mkv",
  "xlsx",
  "xls",
  "csv",
];

// Helper function to get file type
function getFileType(fileName) {
  const ext = fileName.split(".").pop().toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "video";
  if (ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "docx";
  return "other";
}

// Helper function to get file icon
function getFileIcon(fileType) {
  switch (fileType) {
    case "video":
      return <PlayCircleOutlined style={{ fontSize: "14px" }} />;
    case "pdf":
      return <FilePdfOutlined style={{ fontSize: "14px" }} />;
    case "docx":
      return <FileWordOutlined style={{ fontSize: "14px" }} />;
    default:
      return <DocumentTexIcon style={{ fontSize: "14px" }} />;
  }
}

// Helper function to get file tag styles
function getFileTagStyle(fileType) {
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "6px",
    fontWeight: "500",
    fontSize: "12px",
    position: "relative",
    overflow: "hidden",
  };

  const typeStyles = {
    video: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)",
      border: "1px solid rgba(139, 92, 246, 0.4)",
      color: "#a855f7",
      boxShadow: "0 2px 8px rgba(139, 92, 246, 0.12)",
    },
    pdf: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)",
      border: "1px solid rgba(239, 68, 68, 0.4)",
      color: "#ef4444",
      boxShadow: "0 2px 8px rgba(239, 68, 68, 0.12)",
    },
    docx: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)",
      border: "1px solid rgba(59, 130, 246, 0.4)",
      color: "#3b82f6",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.12)",
    },
    other: {
      ...baseStyle,
      background:
        "linear-gradient(135deg, rgba(212, 163, 115, 0.15) 0%, rgba(212, 163, 115, 0.1) 100%)",
      border: "1px solid rgba(212, 163, 115, 0.4)",
      color: "#d4a373",
      boxShadow: "0 2px 8px rgba(212, 163, 115, 0.12)",
    },
  };

  return typeStyles[fileType] || typeStyles.other;
}

// Helper function to get delete button color
function getDeleteButtonColor(fileType) {
  const colorMap = {
    video: "rgba(139, 92, 246, 0.6)",
    pdf: "rgba(239, 68, 68, 0.6)",
    docx: "rgba(59, 130, 246, 0.6)",
    other: "rgba(212, 163, 115, 0.6)",
  };

  return colorMap[fileType] || colorMap.other;
}

// Attachment Tag Component
const AttachmentTag = ({ file, onRemove, index }) => {
  const fileType = getFileType(file.name);
  const tagStyle = getFileTagStyle(fileType);
  const deleteButtonColor = getDeleteButtonColor(fileType);

  return (
    <div style={tagStyle}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          color: "inherit",
          fontSize: "14px",
        }}
      >
        {getFileIcon(fileType)}
      </span>
      <span
        style={{
          maxWidth: "110px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "12px",
        }}
      >
        {file.name}
      </span>
      <button
        onClick={() => onRemove(index)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "14px",
          height: "14px",
          marginLeft: "4px",
          cursor: "pointer",
          borderRadius: "50%",
          background: deleteButtonColor,
          color: "#fff",
          border: "none",
          padding: 0,
          transition: "all 0.2s ease",
          fontSize: "10px",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "scale(1.15)";
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
    </div>
  );
};

const ChatArea = ({
  ticket,
  currentUser,
  initialMessages = [],
  ticketId,
  onTicketClose,
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  //  File States
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const [isLocallyClosed, setIsLocallyClosed] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const { confirm } = Modal;

  const tokenInfoList = useSelector((state) => state.tokenInfoList);
  const token = tokenInfoList?.info?.token;
  const companyId = tokenInfoList?.info?.company?._id || tokenInfoList?.info?.companyId;

  const themeDefault = useSelector((state) => state.themeDefault);
  const primaryColor = themeDefault?.info?.colorPrimary || "#1890ff";

  const handleReload = async () => {
    if (isReloading) return;
    setIsReloading(true);
    try {
      let resolvedId = ticketId;
      // If the ticketId is base64-encoded (not a standard 24-character hexadecimal ObjectId), decode it
      if (ticketId && (ticketId.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(ticketId))) {
        try {
          resolvedId = atob(ticketId);
        } catch (e) {
          // fallback to raw ticketId
        }
      }

      await dispatch(
        adminGetSupportMessageByTicketIdAction(resolvedId, token, companyId, true),
      );
      message.success("Messages reloaded successfully");
    } catch (error) {
      message.error(error.message || "Failed to reload messages");
    } finally {
      setIsReloading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isTicketClosed) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isTicketClosed) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = [];
    let invalidSizeCount = 0;
    let invalidTypeCount = 0;

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (file.size > MAX_FILE_SIZE) {
        invalidSizeCount++;
      } else if (!ALLOWED_EXTENSIONS.includes(ext)) {
        invalidTypeCount++;
      } else {
        validFiles.push(file);
      }
    });

    if (invalidSizeCount > 0)
      message.error(`${invalidSizeCount} file(s) exceeded 10MB limit`);
    if (invalidTypeCount > 0)
      message.error(
        `${invalidTypeCount} file(s) are not supported (docx, pdf, png, jpg, jpeg, mp4, mov, avi, mkv, xlsx, xls, csv only)`,
      );

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  useEffect(() => {
    if (ticket?.status === "closed") {
      setIsLocallyClosed(true);
    }
  }, [ticket?.status]);

  const isTicketClosed = isLocallyClosed;

  useEffect(() => {
    if (Array.isArray(initialMessages)) {
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCloseTicket = () => {
    if (isTicketClosed) return;

    confirm({
      title: "Close Ticket?",
      content: `This will change the status of "${ticket?.subject}" to closed. You won't be able to send messages after this.`,
      okText: "Confirm",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(
            adminUpdateSupportTicketAction(
              { status: "closed" },
              ticketId,
              null,
              null,
              true,
            ),
          );
          setIsLocallyClosed(true);
          message.success("Ticket closed successfully!");
          if (onTicketClose) onTicketClose("closed");
        } catch (error) {
          message.error(error.message || "Failed to close ticket");
        }
      },
    });
  };

  //  FILE HANDLING LOGIC
  const handleFileChange = (e) => {
    if (isTicketClosed) {
      message.warning("Cannot upload files to a closed ticket");
      e.target.value = "";
      return;
    }

    const files = Array.from(e.target.files);
    const validFiles = [];
    let invalidSizeCount = 0;
    let invalidTypeCount = 0;

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (file.size > MAX_FILE_SIZE) {
        invalidSizeCount++;
      } else if (!ALLOWED_EXTENSIONS.includes(ext)) {
        invalidTypeCount++;
      } else {
        validFiles.push(file);
      }
    });

    if (invalidSizeCount > 0)
      message.error(`${invalidSizeCount} file(s) exceeded 10MB limit`);
    if (invalidTypeCount > 0)
      message.error(
        `${invalidTypeCount} file(s) are not supported (docx, pdf, png, jpg, jpeg, mp4, mov, avi, mkv, xlsx, xls, csv only)`,
      );

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }

    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleSendMessage = async () => {
    const cleanMessage = newMessage.replace(/<p><br><\/p>/g, "").trim();
    //  Allow send if there's text OR files
    if (
      (!cleanMessage && selectedFiles.length === 0) ||
      isSending ||
      isTicketClosed
    )
      return;

    setIsSending(true);

    try {
      const messagePayload = {
        supportId: ticketId,
        body: cleanMessage || "Shared files",
        userModel: "user",
        maxAI: false,
      };

      //  Pass files to action
      const savedMessage = await dispatch(
        adminCreateSupportMessageAction(
          messagePayload,
          token,
          companyId,
          true,
          selectedFiles,
        ),
      );

      const messageObj = {
        id: savedMessage?._id || Date.now(),
        senderName: currentUser.name,
        text: newMessage.trim() || "Shared files",
        timestamp: `${new Date().toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })} · ${new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        isCurrentUser: true,
        avatar:
          tokenInfoList?.info?.profileImage?.url ||
          (typeof tokenInfoList?.info?.profileImage === "string"
            ? tokenInfoList.info.profileImage
            : null),
        initials: currentUser.name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2),
        //  Attach files returned from backend (or fallback to local names)
        files:
          savedMessage?.files ||
          selectedFiles.map((f) => ({
            name: f.name,
            url: null,
            mimetype: f.type,
          })),
      };

      setMessages((prev) => [...prev, messageObj]);
      setNewMessage("");
      setSelectedFiles([]); // Clear files after send
      message.success("Message sent successfully");
    } catch (error) {
      console.error("Failed to send message:", error);
      message.error(error.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const sendRef = useRef(null);
  useEffect(() => {
    sendRef.current = handleSendMessage;
  }, [handleSendMessage]);

  const insertFormat = (type) => {
    const quill = textAreaRef.current?.getEditor();
    if (!quill) return;

    quill.focus();
    const range = quill.getSelection(true);

    if (type === "bold") {
      const format = quill.getFormat(range);
      quill.format("bold", !format.bold);
    } else if (type === "italic") {
      const format = quill.getFormat(range);
      quill.format("italic", !format.italic);
    } else if (type === "bullet") {
      const format = quill.getFormat(range);
      quill.format("list", format.list === "bullet" ? false : "bullet");
    } else if (type === "number") {
      const format = quill.getFormat(range);
      quill.format("list", format.list === "ordered" ? false : "ordered");
    } else if (type === "newline") {
      quill.insertText(range.index, "\n");
      quill.setSelection(range.index + 1);
    }
  };

  const insertEmoji = (emoji) => {
    const quill = textAreaRef.current?.getEditor();
    if (!quill) return;

    quill.focus();
    const range = quill.getSelection(true);
    quill.insertText(range.index, emoji);
    quill.setSelection(range.index + emoji.length);
  };

  const modules = React.useMemo(() => ({
    toolbar: false,
    keyboard: {
      bindings: {
        customSend: {
          key: "Enter",
          shiftKey: false,
          handler: function (range, context) {
            if (sendRef.current) {
              sendRef.current();
            }
          },
        },
      },
    },
  }), []);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: "1px solid var(--border-color-split, #e8e8e8)",
        borderRadius: 8,
        overflow: "hidden",
        background: "var(--component-background, #fff)",
        position: "relative",
      }}
    >
      {isDragging && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 50,
            border: `2px dashed ${primaryColor}`,
            borderRadius: "8px",
            background: "rgba(24, 144, 255, 0.08)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "none",
          }}
        >
          <span style={{ fontSize: 40 }}>📎</span>
          <span style={{ color: primaryColor, fontSize: 15, fontWeight: 600 }}>Drop files to attach</span>
        </div>
      )}
      {/* HEADER */}
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--component-background, #fafafa)",
          borderBottom: "1px solid var(--border-color-split, #e8e8e8)",
        }}
      >
        <div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .spin-animation {
              animation: spin 1s linear infinite;
            }
          `}</style>
          <Text style={{ fontSize: 12, color: "var(--text-color-secondary, #8c8c8c)" }}>
            Subject
          </Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <Title level={5} style={{ margin: 0 }}>
              {ticket?.subject || "Support Discussion"}
            </Title>
            <SyncIcon
              onClick={handleReload}
              className={isReloading ? "spin-animation" : ""}
              style={{
                fontSize: 16,
                cursor: "pointer",
                color: primaryColor,
                transition: "transform 0.3s ease",
              }}
            />
          </div>
        </div>
        <div>
          <Button
            type="primary"
            danger
            icon={<CheckCircleOutlined />}
            onClick={handleCloseTicket}
            disabled={isTicketClosed}
            style={{ borderRadius: 6 }}
          >
            {isTicketClosed ? "Ticket Closed" : "Mark as Closed"}
          </Button>
        </div>
      </div>
      <Divider style={{ margin: 0 }} />

      {/* MESSAGES AREA */}
      <div
        className="chat-messages-area"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: 0,
          maxHeight: "100%",
          justifyContent: messages.length === 0 ? "center" : "flex-start",
          background: themeDefault?.info?.newConfig?.colorPrimaryShades?.[0] || "#f0f2f5",
          position: "relative",
        }}
      >
        {messages.length === 0 ? (
          <div style={{ display: "flex", flex: 1, height: "100%", width: "100%", justifyContent: "center", alignItems: "center" }}>
            <Empty
              description={
                <span style={{ color: "var(--text-color-secondary, #8c8c8c)" }}>
                  No messages yet. Start the conversation!
                </span>
              }
            />
          </div>
        ) : (
          (() => {
            let lastDate = null;

            const formatHeaderDate = (dateStr) => {
              if (!dateStr) return "";
              try {
                const messageDate = new Date(dateStr);
                if (isNaN(messageDate.getTime())) {
                  return dateStr;
                }
                const today = new Date();
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);

                const isSameDay = (d1, d2) =>
                  d1.getFullYear() === d2.getFullYear() &&
                  d1.getMonth() === d2.getMonth() &&
                  d1.getDate() === d2.getDate();

                if (isSameDay(messageDate, today)) {
                  return "Today";
                } else if (isSameDay(messageDate, yesterday)) {
                  return "Yesterday";
                }
                return dateStr;
              } catch (e) {
                return dateStr;
              }
            };

            return messages.map((msg) => {
              // Extract date portion (e.g. "Jun 29, 2026") from "Jun 29, 2026 · 02:47 PM"
              const datePart = msg.timestamp ? msg.timestamp.split(" · ")[0] : "";
              const showDateHeader = datePart && datePart !== lastDate;
              if (showDateHeader) {
                lastDate = datePart;
              }

              return (
                <React.Fragment key={msg.id}>
                  {showDateHeader && (
                    <div style={{
                      position: "sticky",
                      top: 4,
                      zIndex: 10,
                      display: "flex",
                      justifyContent: "center",
                      margin: "8px 0 12px 0",
                      width: "100%",
                    }}>
                      <span style={{
                        background: "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(4px)",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#54656f",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}>
                        {formatHeaderDate(datePart)}
                      </span>
                    </div>
                  )}
                  <ChatMessage
                    message={msg}
                    isCurrentUser={msg.senderName === currentUser.name}
                  />
                </React.Fragment>
              );
            });
          })()
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT AREA */}
      <Divider style={{ margin: 0 }} />
      <div style={{ padding: "12px 20px" }}>
        {/*  SELECTED FILES PREVIEW CHIPS */}
        {selectedFiles.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 10,
            }}
          >
            {selectedFiles.map((file, index) => (
              <AttachmentTag
                key={index}
                file={file}
                onRemove={removeFile}
                index={index}
              />
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", background: "var(--component-background, #fafafa)", border: "1px solid var(--border-color-base, #e0e0e0)", borderRadius: 10, padding: "8px 12px" }}>
          {/*  MULTIPLE & SPECIFIC FORMATS */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            multiple
            accept=".docx,.pdf,.png,.jpg,.jpeg,.mp4,.mov,.avi,.mkv,.xlsx,.xls,.csv"
            onChange={handleFileChange}
          />

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <ReactQuill
              ref={textAreaRef}
              theme="bubble"
              value={newMessage}
              onChange={setNewMessage}
              placeholder={
                isTicketClosed ? "This ticket is closed" : "Enter Comment"
              }
              modules={modules}
              readOnly={isSending || isTicketClosed}
              style={{
                background: "transparent",
                width: "100%",
              }}
            />
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Tooltip title={isTicketClosed ? "Ticket is closed" : "Attach files"}>
                <PaperClipIcon
                  onClick={() => !isTicketClosed && fileInputRef.current?.click()}
                  style={{ fontSize: 15, cursor: isTicketClosed ? "not-allowed" : "pointer", color: primaryColor, opacity: isTicketClosed ? 0.5 : 1 }}
                />
              </Tooltip>

              {!isTicketClosed && (
                <Popover
                  content={
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {["😀", "😂", "😍", "👍", "🔥", "🎉", "❤️", "🤔", "👀", "🚀", "👏", "🙌", "💡", "❌", "✅", "⚠️"].map((emoji) => (
                        <span
                          key={emoji}
                          onClick={() => insertEmoji(emoji)}
                          style={{
                            fontSize: 18,
                            cursor: "pointer",
                            padding: "4px",
                            textAlign: "center",
                            borderRadius: "4px",
                            userSelect: "none",
                          }}
                          onMouseEnter={(e) => e.target.style.background = "rgba(0,0,0,0.06)"}
                          onMouseLeave={(e) => e.target.style.background = "transparent"}
                        >
                          {emoji}
                        </span>
                      ))}
                    </div>
                  }
                  trigger="click"
                  placement="top"
                >
                  <Tooltip title="Emojis">
                    <SmileOutlined style={{ fontSize: 15, cursor: "pointer", color: primaryColor }} />
                  </Tooltip>
                </Popover>
              )}

              <div style={{ width: 1, height: 16, background: "var(--border-color-split, #ddd)", margin: "0 2px" }} />
              <Tooltip title="Bold (Ctrl+B)">
                <Button
                  type="text"
                  size="small"
                  icon={<BoldOutlined style={{ fontSize: 13, color: primaryColor }} />}
                  onClick={() => insertFormat("bold")}
                  style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${primaryColor}` }}
                />
              </Tooltip>
              <Tooltip title="Italic (Ctrl+I)">
                <Button
                  type="text"
                  size="small"
                  icon={<ItalicOutlined style={{ fontSize: 13, color: primaryColor }} />}
                  onClick={() => insertFormat("italic")}
                  style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${primaryColor}` }}
                />
              </Tooltip>
              <Tooltip title="Bulleted List">
                <Button
                  type="text"
                  size="small"
                  icon={<UnorderedListOutlined style={{ fontSize: 13, color: primaryColor }} />}
                  onClick={() => insertFormat("bullet")}
                  style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${primaryColor}` }}
                />
              </Tooltip>
              <Tooltip title="Numbered List">
                <Button
                  type="text"
                  size="small"
                  icon={<OrderedListOutlined style={{ fontSize: 13, color: primaryColor }} />}
                  onClick={() => insertFormat("number")}
                  style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${primaryColor}` }}
                />
              </Tooltip>
              <Tooltip title="New Line (Shift+Enter)">
                <Button
                  type="text"
                  size="small"
                  icon={<EnterOutlined style={{ fontSize: 13, color: primaryColor }} />}
                  onClick={() => insertFormat("newline")}
                  style={{ borderRadius: 4, height: 26, width: 26, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${primaryColor}` }}
                />
              </Tooltip>
            </div>
          </div>

          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={isSending}
            onClick={handleSendMessage}
            disabled={
              (!newMessage.trim() && selectedFiles.length === 0) ||
              isTicketClosed
            }
            style={{ height: 42, borderRadius: 8 }}
          >
            Send
          </Button>
        </div>
      </div>

      <style>{`
        .chat-messages-area::-webkit-scrollbar { width: 6px; }
        .ql-container.ql-bubble {
          border: none !important;
          font-family: inherit;
          font-size: 14px;
        }
        .ql-editor, .ql-editor * {
          color: var(--text-color, #000) !important;
        }
        .ql-editor {
          padding: 4px 0 !important;
          min-height: 36px;
          max-height: 120px;
          overflow-y: auto;
        }
        .ql-editor.ql-blank::before {
          left: 0 !important;
          color: var(--text-color-secondary, #aaa) !important;
          font-style: normal !important;
        }
      `}</style>
    </div>
  );
};

export default ChatArea;
